import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { NumericFormat } from 'react-number-format';
import { dateDifference, formatDateText, formatDate } from "./CommonFunctions";
import {
  TextField,
  Button,
  Box,
  Paper,
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import {
  People as PeopleIcon,
  Hotel as HotelIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import TenancyDetailsByLTTI from "./TenancyDetailsByLTTI";
import Header from "./Header";
import PaymentsByTenancyId from "./PaymentsByTenancyId";

const ActiveTenantsReport = () => {
  const now = new Date();
  now.setDate(now.getDate() - 7);
  const [checkin, setCheckin] = useState(formatDate(now));
  const [checkout, setCheckout] = useState(formatDate(new Date()));
  const [reportSummary, setReportSummary] = useState([]);
  const [showTenancyDetailsByLTTIModal, setShowTenancyDetailsByLTTIModal] = useState(false);
  const [showPaymentDetailsByTenancyIdModal, setShowPaymentDetailsByTenancyIdModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
      getAllActiveTenantsInfo(null, checkin, checkout);
    }, []);

  const columns = [
    {
      field: 'roomid',
      headerName: 'Room Number',
      width: 120,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <Link onClick={(e) => {}}>{currentRow.roomid}</Link>
      }
    },
    {
      field: 'customerNames',
      headerName: 'Names',
      width: 250,
      align: 'left'
    },
    {
      field: 'phoneNumbers',
      headerName: 'Phone Numbers',
      width: 250,
      align: 'left'
    },
    {
      field: 'checkin',
      headerName: 'Tenancy Period',
      width: 280,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        const tenancyDetails = currentRow.tenancyDetails;
        if (!tenancyDetails || tenancyDetails.length === 0) return '';
        const lastTenancyIndex = tenancyDetails.length - 1;
        const first = tenancyDetails[0].checkin;
        const last = params.row.tenancyDetails[lastTenancyIndex].actualCheckoutDate ?
          params.row.tenancyDetails[lastTenancyIndex].actualCheckoutDate :
          params.row.tenancyDetails[lastTenancyIndex].checkout;

        return <Link onClick={(e) => {
          setSelectedRow(params.row);
          setShowTenancyDetailsByLTTIModal(true);
        }}>
          {formatDateText(first) + ' to ' + formatDateText(last)}
        </Link>
      }
    },
    {
      field: 'stayDuration',
      headerName: 'Stay Duration',
      width: 150,
      align: 'left',
      renderCell: (params) => {
        const stayDuration = params.row.stayDuration;
        return stayDuration + " days";
      }
    },
    {
      field: 'totalRent',
      headerName: 'Total',
      width: 120,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.totalRent} thousandSeparator prefix={'$'} displayType="text" />
      }
    },
    {
      field: 'amountPaid',
      headerName: 'Amount Paid',
      width: 120,
      align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <Link onClick={(e) => {
          setSelectedRow(params.row);
          setShowPaymentDetailsByTenancyIdModal(true);
        }}>
          <NumericFormat value={currentRow.amountPaid} thousandSeparator prefix={'$'} displayType="text" />
        </Link>
      }
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 120,
      align: 'right',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.balance} thousandSeparator prefix={'$'} displayType="text" />
      }
    },
  ];

  const preprocessData = (data) => {
    if (!data || data.length === 0) return [];
    
    return data.map(row => {
      if (!row.tenancyDetails || row.tenancyDetails.length === 0) {
        return {
          ...row,
          roomid: null,
          lastCheckin: null,
          overStay: null,
          stayDuration: 0,
          amountPaid: 0,
          customerNames: '',
          phoneNumbers: '',
          balance: row.totalRent || 0
        };
      }

      const lastTenancyIndex = row.tenancyDetails.length - 1;
      const lastTenancy = row.tenancyDetails[lastTenancyIndex];
      const first = row.tenancyDetails[0].checkin;
      const last = row.tenancyDetails[lastTenancyIndex].actualCheckoutDate ?
        row.tenancyDetails[lastTenancyIndex].actualCheckoutDate :
        row.tenancyDetails[lastTenancyIndex].checkout;
      const customersInfo = row.customerDetails || [];
      var names = "";
      var phoneNumbers = "";
      customersInfo.map((row => {
        const fullName = row.fname + " " + row.lname;
        if (names.length == 0) {
          names = fullName;
          phoneNumbers = row.phone;
        } else {
          names = names + ", " + fullName;
          phoneNumbers = phoneNumbers + ", " + row.phone;
        }
      }));
      var amountPaid = 0;
      const payments = row.payments || [];
      console.log(payments);
      payments.map((row) => {
        row.map((sub) => {
          var temp = 0;
          if (sub.cashAmount !== null) {
            temp += sub.cashAmount;
          }
          if (sub.creditCardAmount !== null) {
            temp += sub.creditCardAmount;
          }
          if (sub.otherAmount !== null) {
            temp += sub.otherAmount;
          }
          amountPaid = amountPaid + temp;
        })
      })
      console.log(amountPaid);

      return {
        ...row,
        roomid: row.tenancyDetails.length > 0 ? row.tenancyDetails[lastTenancyIndex].roomid : null,
        lastCheckin: lastTenancy.checkin,
        overStay: lastTenancy.actualCheckoutDate ?
          null : dateDifference(lastTenancy.checkout, new Date()),
        stayDuration: dateDifference(first, last),
        amountPaid: amountPaid,
        customerNames: names,
        phoneNumbers: phoneNumbers,
        balance: (row.totalRent || 0) - amountPaid
      }
    });
  };

  const getAllActiveTenantsInfo = async (e, searchStartDate, searchEndDate) => {
    try {
      const response = await axios.post(`${url}/getActiveTenants`, {
        searchStartDate,
        searchEndDate
      });
      if (response) {
        console.log(response.data);
        setReportSummary(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const processedData = preprocessData(reportSummary);
  const totalTenants = processedData.length;
  const totalRent = processedData.reduce((sum, row) => sum + (row.totalRent || 0), 0);
  const totalPaid = processedData.reduce((sum, row) => sum + (row.amountPaid || 0), 0);
  const totalBalance = processedData.reduce((sum, row) => sum + (row.balance || 0), 0);

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', 
                  alignItems: 'center', mb: 3,
                    pb: 2,borderBottom: 1,borderColor: "divider", }}>
           <Typography variant="h6" fontWeight="600">
              Active Tenants Report
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={checkin}
                onChange={(e) => setCheckin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={checkout}
                onChange={(e) => setCheckout(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="contained"
                onClick={(e) => getAllActiveTenantsInfo(e, checkin, checkout)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <PeopleIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total Tenants</Typography>
                      <Typography variant="h6" fontWeight="600">{totalTenants}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total Rent</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalRent} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Amount Paid</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalPaid} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'warning.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total Balance</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalBalance} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={processedData}
              columns={columns}
              getRowId={(row) => row.longTermTenancyId}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "grey.50",
                  borderBottom: 2,
                  borderColor: "divider",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>

      {showTenancyDetailsByLTTIModal && <TenancyDetailsByLTTI
        tenancyDetailsProps={selectedRow}
        showTenancyDetailsByLTTIModal={showTenancyDetailsByLTTIModal}
        setShowTenancyDetailsByLTTIModal={setShowTenancyDetailsByLTTIModal}
      />}
      {showPaymentDetailsByTenancyIdModal && <PaymentsByTenancyId
        paymentDetailsProps={selectedRow}
        showPaymentDetailsByTenancyIdModal={showPaymentDetailsByTenancyIdModal}
        setShowPaymentDetailsByTenancyIdModal={setShowPaymentDetailsByTenancyIdModal}
      />}
    </>
  );
}

export default ActiveTenantsReport;